import { captureAppError } from '@leagueplatform/observability';
import { getPartnerIdentityToken } from 'api/get-partner-id-token';
import { FetchResult } from './fetchDataWithToken';

/**
 * Fetch data with a partner authorization token, specifically for secured PDF links.
 * @param url - The URL to fetch data from.
 * @param partnerId - The partner ID to get tokens for.
 * @returns A result containing either content (Blob for PDF) and content type, or an error.
 */
export async function fetchDataWithPartnerToken(
  url: string,
  partnerId: string,
): Promise<FetchResult> {
  try {
    // Get partner token
    const partnerTokenResponse = await getPartnerIdentityToken(partnerId);
    const partnerToken = partnerTokenResponse.data.attributes.accessToken;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${partnerToken}`,
        Accept: 'application/pdf',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    }

    const rawContentType =
      response.headers.get('Content-Type')?.toLowerCase() || '';

    if (rawContentType.includes('application/pdf')) {
      const content = await response.blob();
      return { content, contentType: 'application/pdf' };
    }

    throw new Error(`Unsupported content type: ${rawContentType}`);
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    const isDnsError =
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('net::ERR_NAME_NOT_RESOLVED');

    captureAppError(err instanceof Error ? err : new Error(errorMessage), {
      context: { contextDetails: { partnerId, url } },
    });

    return {
      error: {
        message: isDnsError
          ? 'Unable to reach the server (DNS resolution failed)'
          : errorMessage,
        code: 500,
      },
    };
  }
}
